import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SpaceBetween } from '@amzn/awsui-components-console';
import {
  ThinkingPanel,
  CodeBlock,
  AccessStatusTable,
  AccessRequestGuide,
  ActionCard,
  OutputPreview,
  FormPanel,
  RequirementReviewCard,
  JobDashboard,
  ArtifactReviewCard,
  MermaidDiagram,
  SuggestedActions,
} from '../../../../chat';
import NotificationBanner from '../../../../chat/NotificationBanner';
import {
  toThinkingPanelProps,
  toCodeBlockProps,
  toAccessStatusProps,
  toActionCardProps,
  toOutputPreviewProps,
  toNotificationProps,
  toFormPanelProps,
  toRequirementReviewProps,
  toJobDashboardProps,
  toArtifactReviewProps,
  toSuggestedActionsProps,
} from '../../../../chat/adapters';
import { markdownComponents } from '../../../../chat/markdownComponents';
import type { AgentMessageRendererProps } from './types';
import type { AgentMessagePart } from '../../../../../types/agent-messages';

const renderPart = (part: AgentMessagePart, index: number, onAction?: (action: string) => void) => {
  switch (part.type) {
    case 'thinking':
      return (
        <ThinkingPanel
          key={`thinking-${index}`}
          {...toThinkingPanelProps(part)}
        />
      );

    case 'text':
      return (
        <div key={`text-${index}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{part.content}</ReactMarkdown>
        </div>
      );

    case 'codeBlock':
      return (
        <CodeBlock
          key={`code-${index}`}
          {...toCodeBlockProps(part)}
        />
      );

    case 'mermaid':
      return (
        <MermaidDiagram
          key={`mermaid-${index}`}
          content={part.content}
          title="Data Flow Diagram"
        />
      );

    case 'accessStatus': {
      const accessProps = toAccessStatusProps(part);
      // Collect denied / role_no_access tables across all groups
      const deniedTables: string[] = [];
      accessProps.groups.forEach((g: { tables: Array<{ name: string; status: string }> }) => {
        g.tables.forEach((t: { name: string; status: string }) => {
          if (t.status === 'denied' || t.status === 'role_no_access') {
            deniedTables.push(t.name);
          }
        });
      });

      return (
        <React.Fragment key={`access-${index}`}>
          <AccessStatusTable {...accessProps} />
          {deniedTables.length > 0 && (
            <AccessRequestGuide deniedTables={deniedTables} />
          )}
        </React.Fragment>
      );
    }

    case 'actionCard':
      return (
        <ActionCard
          key={`action-${index}`}
          {...toActionCardProps(part, onAction)}
        />
      );

    case 'accessRequestGuide': {
      const guideTables: string[] = (part as any).deniedTables || [];
      return guideTables.length > 0 ? (
        <AccessRequestGuide key={`access-guide-${index}`} deniedTables={guideTables} />
      ) : null;
    }

    case 'outputPreview':
      return (
        <OutputPreview
          key={`preview-${index}`}
          {...toOutputPreviewProps(part)}
        />
      );

    case 'notification':
      return (
        <NotificationBanner
          key={`notif-${index}`}
          {...toNotificationProps(part, onAction)}
        />
      );

    case 'form':
      return (
        <FormPanel
          key={`form-${index}`}
          {...toFormPanelProps(
            part,
            onAction ? () => onAction('submitAccessRequest') : undefined,
            onAction ? () => onAction('cancelAccessRequest') : undefined,
          )}
        />
      );

    case 'requirementReview':
      return (
        <RequirementReviewCard
          key={`req-review-${index}`}
          {...toRequirementReviewProps(part, onAction)}
        />
      );

    case 'jobDashboard':
      return (
        <JobDashboard
          key={`job-dash-${index}`}
          {...toJobDashboardProps(part, onAction)}
        />
      );

    case 'artifactReview':
      return (
        <ArtifactReviewCard
          key={`artifact-review-${index}`}
          {...toArtifactReviewProps(part, onAction)}
        />
      );

    case 'suggestedActions':
      return (
        <SuggestedActions
          key={`suggested-${index}`}
          {...toSuggestedActionsProps(part, onAction)}
        />
      );

    default:
      return null;
  }
};

const AgentMessageRenderer: React.FC<AgentMessageRendererProps> = ({ parts, onAction }) => {
  if (!parts || parts.length === 0) return null;

  return (
    <SpaceBetween size="m" direction="vertical">
      {parts.map((part, index) => renderPart(part, index, onAction))}
    </SpaceBetween>
  );
};

export default AgentMessageRenderer;
